// utils/downloadQuestions.js
import {
  Document, Packer, Paragraph, TextRun,
  HeadingLevel, AlignmentType, LevelFormat,
} from "docx";
import { saveAs } from "file-saver";

const levelColor = {
  beginner: "28A745",
  intermediate: "D97706",
  advanced:     "DC3545",
};

const levelBadge = (level) =>
  new Paragraph({
    children: [
      new TextRun({
        text: `  ${level.toUpperCase()}  `,
        bold: true,
        size: 20,
        color: levelColor[level] || "333333",
      }),
    ],
    spacing: { before: 160, after: 80 },
  });

const dividerParagraph = () =>
  new Paragraph({
    children: [new TextRun({ text: "" })],
    spacing: { before: 480 },
    border: { top: { style: "single", size: 6, color: "CCCCCC", space: 8 } },
  });

// ── Render manual / section-wise questions ─────────────────────────────
function renderManualSection(sectionData, manualQuestions, headingColor, children) {
  const checked = (sectionData || []).filter((s) => s.checked);
  if (!checked.length) return;

  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: [new TextRun({ text: "Section-wise Manual Questions", bold: true, size: 30, color: headingColor })],
      spacing: { before: 400, after: 200 },
    })
  );

  checked.forEach((section) => {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun({ text: section.name, bold: true, size: 26 })],
        spacing: { before: 280, after: 120 },
        border: { bottom: { style: "single", size: 4, color: "E0E0E0", space: 1 } },
      })
    );
    ["beginner", "intermediate", "advanced"].forEach((level) => {
      const qs = manualQuestions?.[section.name]?.questions?.[level] || [];
      if (!qs.length) return;
      children.push(levelBadge(level));
      qs.forEach((q, i) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `${i + 1}.  `, bold: true, size: 22 }),
              new TextRun({ text: q, size: 22 }),
            ],
            spacing: { after: 80 },
            indent: { left: 360 },
          })
        );
      });
    });
  });
}

// ── Render AI generated questions ──────────────────────────────────────
function renderGeneratedSection(generatedQuestions, headingColor, children) {
  if (!Object.keys(generatedQuestions || {}).length) return;

  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: [new TextRun({ text: "AI Generated Questions", bold: true, size: 30, color: headingColor })],
      spacing: { before: 480, after: 200 },
    })
  );

  Object.entries(generatedQuestions).forEach(([skill, levels]) => {
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun({ text: skill, bold: true, size: 26 })],
        spacing: { before: 280, after: 120 },
        border: { bottom: { style: "single", size: 4, color: "CFE2FF", space: 1 } },
      })
    );
    ["beginner", "intermediate", "advanced"].forEach((level) => {
      const qs = (levels[level] || []).filter((q) => q?.text);
      if (!qs.length) return;
      children.push(levelBadge(level));
      qs.forEach((qObj, i) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: `${i + 1}.  `, bold: true, size: 22 }),
              new TextRun({ text: qObj.text, size: 22 }),
            ],
            spacing: { after: 80 },
            indent: { left: 360 },
          })
        );
      });
    });
  });
}

// ── Render screening questions ─────────────────────────────────────────
function renderScreeningSection(screeningQuestions, headingText, headingColor, children) {
  if (!Object.keys(screeningQuestions || {}).length) return;

  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: [new TextRun({ text: headingText, bold: true, size: 30, color: headingColor })],
      spacing: { before: 400, after: 200 },
    })
  );

  Object.entries(screeningQuestions).forEach(([category, questions]) => {
    if (!Array.isArray(questions) || !questions.length) return;

    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [
          new TextRun({
            text: category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            bold: true,
            size: 26,
          }),
        ],
        spacing: { before: 240, after: 100 },
        border: { bottom: { style: "single", size: 4, color: "E0E0E0", space: 1 } },
      })
    );

    questions.forEach((q, i) => {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${i + 1}.  `, bold: true, size: 22 }),
            new TextRun({ text: q, size: 22 }),
          ],
          spacing: { after: 80 },
          indent: { left: 360 },
        })
      );
    });
  });
}

// ── Build document ─────────────────────────────────────────────────────
function buildQuestionsDoc({
  currentGeneratedQuestions = {},
  currentManualQuestions = {},
  sectionData = [],
  previousGeneratedQuestions = {},
  previousManualQuestions = {},
  currentScreeningQuestions = {},
  previousScreeningQuestions = {},
}) {
  const children = [];

  // Title
  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: "All Interview Questions", bold: true, size: 36 })],
      spacing: { after: 300 },
    })
  );

  // ── CURRENT QUESTIONS ────────────────────────────────────────────────
  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_2,
      children: [new TextRun({ text: "Current Questions", bold: true, size: 32, color: "1A1A2E" })],
      spacing: { before: 240, after: 160 },
      border: { bottom: { style: "single", size: 8, color: "1A1A2E", space: 4 } },
    })
  );

  renderManualSection(sectionData, currentManualQuestions, "F47F22", children);
  renderGeneratedSection(currentGeneratedQuestions, "007BFF", children);
  renderScreeningSection(currentScreeningQuestions, "Screening Call Questions", "6F42C1", children);

  // ── PREVIOUS QUESTIONS ───────────────────────────────────────────────
  const hasPrevGenerated  = Object.keys(previousGeneratedQuestions).length > 0;
  const hasPrevManual     = Object.keys(previousManualQuestions).length > 0;
  const hasPrevScreening  = Object.keys(previousScreeningQuestions).length > 0;

  if (hasPrevGenerated || hasPrevManual || hasPrevScreening) {
    children.push(dividerParagraph());
    children.push(
      new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [
          new TextRun({
            text: "Previous Questions (Before Last Regeneration)",
            bold: true,
            size: 32,
            color: "6C757D",
          }),
        ],
        spacing: { before: 240, after: 200 },
        border: { bottom: { style: "single", size: 8, color: "6C757D", space: 4 } },
      })
    );

    if (hasPrevManual) {
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          children: [new TextRun({ text: "Section-wise Questions", bold: true, size: 26, color: "F47F22" })],
          spacing: { before: 280, after: 160 },
        })
      );
      Object.entries(previousManualQuestions).forEach(([sectionName, data]) => {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: sectionName, bold: true, size: 24 })],
            spacing: { before: 200, after: 100 },
          })
        );
        ["beginner", "intermediate", "advanced"].forEach((level) => {
          const qs = data?.questions?.[level] || [];
          if (!qs.length) return;
          children.push(levelBadge(level));
          qs.forEach((q, i) => {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `${i + 1}.  `, bold: true, size: 22 }),
                  new TextRun({ text: q, size: 22 }),
                ],
                spacing: { after: 80 },
                indent: { left: 360 },
              })
            );
          });
        });
      });
    }

    if (hasPrevGenerated) {
      children.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          children: [new TextRun({ text: "AI Generated Questions", bold: true, size: 26, color: "007BFF" })],
          spacing: { before: 320, after: 160 },
        })
      );
      Object.entries(previousGeneratedQuestions).forEach(([skill, levels]) => {
        children.push(
          new Paragraph({
            children: [new TextRun({ text: skill, bold: true, size: 24 })],
            spacing: { before: 200, after: 100 },
          })
        );
        ["beginner", "intermediate", "advanced"].forEach((level) => {
          const qs = (levels[level] || []).filter((q) => q?.text || typeof q === "string");
          if (!qs.length) return;
          children.push(levelBadge(level));
          qs.forEach((qObj, i) => {
            const text = typeof qObj === "string" ? qObj : qObj.text;
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `${i + 1}.  `, bold: true, size: 22 }),
                  new TextRun({ text, size: 22 }),
                ],
                spacing: { after: 80 },
                indent: { left: 360 },
              })
            );
          });
        });
      });
    }

    if (hasPrevScreening) {
      renderScreeningSection(
        previousScreeningQuestions,
        "Previous Screening Call Questions",
        "6C757D",
        children
      );
    }
  }

  // ── Document ──────────────────────────────────────────────────────────
  return new Document({
    numbering: {
      config: [
        {
          reference: "numbers",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.LEFT,
              style: { paragraph: { indent: { left: 720, hanging: 360 } } },
            },
          ],
        },
      ],
    },
    styles: {
      default: { document: { run: { font: "Arial", size: 24 } } },
      paragraphStyles: [
        {
          id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 36, bold: true, font: "Arial", color: "1A1A1A" },
          paragraph: { spacing: { before: 240, after: 240 }, outlineLevel: 0 },
        },
        {
          id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 30, bold: true, font: "Arial" },
          paragraph: { spacing: { before: 200, after: 160 }, outlineLevel: 1 },
        },
        {
          id: "Heading3", name: "Heading 3", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 26, bold: true, font: "Arial" },
          paragraph: { spacing: { before: 160, after: 120 }, outlineLevel: 2 },
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            size: { width: 12240, height: 15840 },
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        children,
      },
    ],
  });
}

export const downloadQuestionsDocx = async ({
  currentGeneratedQuestions = {},
  currentManualQuestions = {},
  sectionData = [],
  previousGeneratedQuestions = {},
  previousManualQuestions = {},
  currentScreeningQuestions = {},
  previousScreeningQuestions = {},
  jobTitle = "Job",
}) => {
  const doc = buildQuestionsDoc({
    currentGeneratedQuestions,
    currentManualQuestions,
    sectionData,
    previousGeneratedQuestions,
    previousManualQuestions,
    currentScreeningQuestions,
    previousScreeningQuestions,
  });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${jobTitle.replace(/[^a-z0-9]/gi, "_")}_Questions.docx`);
};

// ── Screening-only download (for Screening Config tab) ─────────────────
export const downloadScreeningDocx = async ({
  currentScreeningQuestions = {},
  previousScreeningQuestions = {},
  jobTitle = "Job",
}) => {
  const children = [];

  children.push(
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: "Screening Call Questions", bold: true, size: 36 })],
      spacing: { after: 300 },
    })
  );

  renderScreeningSection(currentScreeningQuestions, "Current Screening Questions", "6F42C1", children);

  if (Object.keys(previousScreeningQuestions).length > 0) {
    children.push(dividerParagraph());
    renderScreeningSection(
      previousScreeningQuestions,
      "Previous Screening Questions (Before Last Regeneration)",
      "6C757D",
      children
    );
  }

  const doc = new Document({
    styles: {
      default: { document: { run: { font: "Arial", size: 24 } } },
      paragraphStyles: [
        {
          id: "Heading1", name: "Heading 1", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 36, bold: true, font: "Arial", color: "1A1A1A" },
          paragraph: { spacing: { before: 240, after: 240 }, outlineLevel: 0 },
        },
        {
          id: "Heading2", name: "Heading 2", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 30, bold: true, font: "Arial" },
          paragraph: { spacing: { before: 200, after: 160 }, outlineLevel: 1 },
        },
        {
          id: "Heading3", name: "Heading 3", basedOn: "Normal", next: "Normal", quickFormat: true,
          run: { size: 26, bold: true, font: "Arial" },
          paragraph: { spacing: { before: 160, after: 120 }, outlineLevel: 2 },
        },
      ],
    },
    sections: [
      {
        properties: {
          page: {
            size: { width: 12240, height: 15840 },
            margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
          },
        },
        children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${jobTitle.replace(/[^a-z0-9]/gi, "_")}_Screening_Questions.docx`);
};